import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCatalogSearch, CatalogItem } from '../../../hooks/useCatalogSearch';
import { useDebounce } from '../../../hooks/useDebounce';
import { Popover, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'cmdk';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';

export interface CatalogSelectProps {
  /**
   * Nombre del catálogo en la API (ej. 'categories', 'products', 'departments')
   */
  catalog: string;
  /**
   * Valor seleccionado actualmente (ID)
   */
  value?: string | number;

  selectedItem?: CatalogItem;
  /**
   * Callback ejecutado al seleccionar un elemento.
   * Recibe el ID del elemento o el objeto completo si returnObject es true.
   */
  onChange: (value: any) => void;
  /**
   * Texto por defecto cuando no hay selección
   */
  placeholder?: string;
  /**
   * Campos opcionales a retornar de la API (ej. ['id', 'name', 'code'])
   */
  fields?: string[];
  /**
   * Si es true, onChange devolverá el objeto completo en lugar de solo el ID
   */
  returnObject?: boolean;
  className?: string;
  selectedId?: string | number;
}

/**
 * CatalogSelect
 * Componente reutilizable para seleccionar elementos de catálogos remotos
 * con soporte para Infinite Scroll y búsqueda con debounce.
 *
 * @example
 * ```tsx
 * const [categoryId, setCategoryId] = useState('');
 * 
 * return (
 *   <CatalogSelect
 *     catalog="categories"
 *     placeholder="Selecciona una categoría..."
 *     value={categoryId}
 *     onChange={setCategoryId}
 *   />
 * );
 * ```
 */
export function CatalogSelect({
  catalog,
  value,
  selectedItem,
  onChange,
  placeholder = 'Seleccionar...',
  fields = [],
  returnObject = false,
  className = '',
}: CatalogSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  // TanStack Query Infinite Query
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError
  } = useCatalogSearch({
    catalog,
    search: debouncedSearch,
    fields,
    enabled: open, // Solo buscar cuando el popover esté abierto
    selectedId: value
  });

  // Aplanar las páginas de la respuesta infinita
  const items = React.useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  // Encontrar el item seleccionado para mostrar su nombre
  const foundItem = React.useMemo(() => {
    return items.find((item) => String(item.id) === String(value));
  }, [items, value]);

  const displayItem = foundItem ?? selectedItem;

  // Observer para infinite scroll
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading || isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-expanded={open}
          className={`flex h-10 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
        >
          <span className="truncate">
            {displayItem
              ? displayItem.name ||
              displayItem.title ||
              displayItem.description ||
              `ID: ${displayItem.id}`
              : <span className="text-slate-500">{placeholder}</span>}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-50 bg-white border border-slate-200 rounded-md shadow-md" align="start">
        <Command className="w-full rounded-md" shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandInput
              placeholder="Buscar..."
              value={search}
              onValueChange={setSearch}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <CommandList className="max-h-60 overflow-y-auto p-1">
            {isLoading && (
              <div className="py-6 text-center text-sm text-slate-500 flex items-center justify-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando...
              </div>
            )}

            {!isLoading && items.length === 0 && (
              <CommandEmpty className="py-6 text-center text-sm text-slate-500">
                No se encontraron resultados.
              </CommandEmpty>
            )}

            {!isLoading && isError && (
              <div className="py-6 text-center text-sm text-red-500">
                Ocurrió un error al cargar.
              </div>
            )}

            <CommandGroup>
              {items.map((item: CatalogItem, index: number) => {
                const label = item.name || item.title || item.description || `ID: ${item.id}`;
                const isSelected = String(value) === String(item.id);
                const isLast = items.length === index + 1;

                return (
                  <CommandItem
                    key={`${item.id}-${index}`}
                    value={String(item.id)}
                    ref={isLast ? lastItemRef : null}
                    onSelect={() => {
                      onChange(returnObject ? item : String(item.id));
                      setOpen(false);
                    }}
                    className={`relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-slate-100 data-[selected=true]:text-slate-900 ${isSelected ? 'bg-slate-100 text-slate-900 font-medium' : ''
                      }`}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'
                        }`}
                    />
                    {label}
                  </CommandItem>
                );
              })}
            </CommandGroup>

            {isFetchingNextPage && (
              <div className="py-4 text-center text-sm text-slate-500 flex items-center justify-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando más...
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
